import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw, Search, Shield, UserX } from "lucide-react";
import { supabase } from "../../supabaseClient";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

// Status do preapproval no Mercado Pago. A tabela `assinaturas` guarda o valor
// cru vindo da API, sem tradução — ver backend/tesourinhos-webhook-plan.md.
const STATUS_LABEL = {
  authorized: { texto: "Ativa", cor: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paused: { texto: "Pausada", cor: "bg-amber-50 text-amber-700 border-amber-200" },
  cancelled: { texto: "Cancelada", cor: "bg-red-50 text-red-700 border-red-200" },
  pending: { texto: "Pendente", cor: "bg-slate-50 text-slate-700 border-slate-200" },
};

const formatarValor = (valor) =>
  valor == null
    ? "—"
    : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor);

const formatarData = (data) =>
  data ? new Date(data).toLocaleDateString("pt-BR") : "—";

const StatusBadge = ({ status }) => {
  const info = STATUS_LABEL[status];

  // Status desconhecido é exibido cru de propósito: o MP pode introduzir
  // valores novos, e escondê-los atrás de um rótulo genérico esconderia o fato.
  if (!info) {
    return (
      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
        {status || "—"}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className={info.cor}>
      {info.texto}
    </Badge>
  );
};

const MetricCard = ({ titulo, valor, icone, className, alerta }) => (
  <Card className={className}>
    <CardContent className="flex items-center gap-3 py-4">
      <div className={alerta ? "text-amber-500" : "text-indigo-400"}>{icone}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{titulo}</p>
        <p className="text-lg font-bold text-foreground">{valor}</p>
      </div>
    </CardContent>
  </Card>
);

const AssinaturaRow = ({ assinatura }) => {
  const precisaAtencao =
    !assinatura.usuario_id ||
    !assinatura.plano ||
    assinatura.plano === "desconhecido";

  return (
    <div
      id={`admin-assinatura-${assinatura.id}`}
      className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border p-3 ${
        precisaAtencao ? "border-amber-200 bg-amber-50/40" : "border-warm-100 bg-white"
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-foreground truncate">
          {assinatura.usuario
            ? `${assinatura.usuario.nome ?? ""} ${assinatura.usuario.sobrenome ?? ""}`.trim()
            : "Sem usuário vinculado"}
        </p>
        <p className="text-xs text-muted-foreground truncate">{assinatura.email || "—"}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="outline" className="capitalize">
          {assinatura.plano || "—"}
        </Badge>
        <StatusBadge status={assinatura.status} />
        <span className="font-medium text-foreground">{formatarValor(assinatura.valor)}</span>
        <span className="text-muted-foreground">
          Próx.: {formatarData(assinatura.proximo_pagamento)}
        </span>
      </div>

      {precisaAtencao && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700">
          <AlertTriangle size={14} />
          <span>
            {!assinatura.usuario_id
              ? "E-mail do pagador não casa com nenhum cadastro"
              : "Plano não identificado pelo webhook"}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Assinaturas do Mercado Pago — leitura da tabela `assinaturas`, alimentada
 * pelo webhook PHP (Hostinger). É esta tabela que decide quem tem assinatura
 * ativa, e até aqui ela não tinha nenhuma tela: o admin geria `produtos`/
 * `vendas`, modelo que o webhook substituiu para assinaturas.
 *
 * Somente leitura de propósito: a fonte da verdade é o Mercado Pago. Editar um
 * status aqui divergiria do MP até o próximo evento do webhook sobrescrever a
 * mudança. Ações de cobrança (pausar, cancelar) são feitas no painel do MP.
 */
const AssinaturasMPContent = () => {
  const [assinaturas, setAssinaturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");

  const fetchAssinaturas = async () => {
    setLoading(true);
    setErro(null);

    try {
      const { data, error } = await supabase
        .from("assinaturas")
        .select("*, usuario:usuarios(nome, sobrenome, email)")
        .order("atualizado_em", { ascending: false });

      if (error) throw error;
      setAssinaturas(data || []);
    } catch (error) {
      console.error("Erro ao buscar assinaturas:", error);
      // Um erro aqui é indistinguível de "não há assinaturas" se ficar só no
      // console — e uma lista vazia por falha de RLS pareceria negócio parado.
      setErro(error.message || "Falha ao carregar assinaturas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssinaturas();
  }, []);

  const metricas = useMemo(() => {
    const ativas = assinaturas.filter((a) => a.status === "authorized");

    return {
      ativas: ativas.length,
      receita: ativas.reduce((soma, a) => soma + (Number(a.valor) || 0), 0),
      semUsuario: assinaturas.filter((a) => !a.usuario_id).length,
      planoDesconhecido: assinaturas.filter(
        (a) => !a.plano || a.plano === "desconhecido",
      ).length,
    };
  }, [assinaturas]);

  const assinaturasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return assinaturas.filter((a) => {
      const casaStatus = statusFiltro === "todos" || a.status === statusFiltro;

      if (!termo) return casaStatus;

      const nome = a.usuario
        ? `${a.usuario.nome ?? ""} ${a.usuario.sobrenome ?? ""}`.toLowerCase()
        : "";

      const casaTermo =
        (a.email || "").toLowerCase().includes(termo) ||
        nome.includes(termo) ||
        (a.plano || "").toLowerCase().includes(termo);

      return casaStatus && casaTermo;
    });
  }, [assinaturas, busca, statusFiltro]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Assinaturas (Mercado Pago)</h1>
          <p className="text-sm text-muted-foreground">
            Sincronizadas pelo webhook. O Mercado Pago é a fonte da verdade — para
            pausar ou cancelar, use o painel do MP.
          </p>
        </div>
        <Button
          id="admin-assinaturas-btn-recarregar"
          variant="outline"
          size="sm"
          onClick={fetchAssinaturas}
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Recarregar
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          titulo="Assinaturas ativas"
          valor={metricas.ativas}
          icone={<Shield size={22} />}
          className="bg-indigo-50 border-indigo-100"
        />
        <MetricCard
          titulo="Receita recorrente"
          valor={formatarValor(metricas.receita)}
          icone={<Shield size={22} />}
          className="bg-emerald-50 border-emerald-100"
        />
        <MetricCard
          titulo="Sem usuário vinculado"
          valor={metricas.semUsuario}
          icone={<UserX size={22} />}
          className="bg-amber-50 border-amber-100"
          alerta
        />
        <MetricCard
          titulo="Plano não identificado"
          valor={metricas.planoDesconhecido}
          icone={<AlertTriangle size={22} />}
          className="bg-amber-50 border-amber-100"
          alerta
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="admin-assinaturas-busca"
            type="text"
            placeholder="Buscar por nome, e-mail ou plano..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFiltro} onValueChange={setStatusFiltro}>
          <SelectTrigger className="sm:w-48" id="admin-assinaturas-filtro-status">
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            <SelectItem value="authorized">Ativa</SelectItem>
            <SelectItem value="paused">Pausada</SelectItem>
            <SelectItem value="cancelled">Cancelada</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {erro && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4 text-sm text-red-700">
            <p className="font-semibold">Não foi possível carregar as assinaturas.</p>
            <p className="text-xs mt-1">{erro}</p>
          </CardContent>
        </Card>
      )}

      {loading && !erro && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground">
            Carregando assinaturas...
          </CardContent>
        </Card>
      )}

      {!loading && !erro && assinaturasFiltradas.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground">
            {assinaturas.length === 0
              ? "Nenhuma assinatura registrada pelo webhook até agora."
              : "Nenhuma assinatura corresponde ao filtro."}
          </CardContent>
        </Card>
      )}

      {!loading && !erro && assinaturasFiltradas.length > 0 && (
        <div className="space-y-2">
          {assinaturasFiltradas.map((assinatura) => (
            <AssinaturaRow key={assinatura.id} assinatura={assinatura} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AssinaturasMPContent;
